import { inject, Injectable } from '@angular/core';
import { shareReplay } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ThemeBaseSchema,
  ThemeButtonStateSchema,
  ThemeButtonVariantSchema,
  ThemeCardSchema,
  ThemePrimarySchema,
  ThemeSchema,
  ThemeSecondarySchema,
  ThemeTabBarSchema,
} from '@app/schemas/theme';
import { ConfigService } from '@app/services';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {

  private readonly configService = inject(ConfigService);

  public readonly config$ = this.configService.config$.pipe(
    map(({ theme }) => theme),
    shareReplay(1),
  );

  public static baseStyles(theme: ThemeBaseSchema | undefined) {
    if (!theme) {
      return {};
    }

    return {
      '--base-background-color': theme.background?.color,
      '--base-foreground-color': theme.foreground?.color,
      '--base-border-radius': theme.border?.radius,
    };
  }

  public static primaryStyles(theme: ThemePrimarySchema | undefined) {
    if (!theme) {
      return {};
    }

    return {
      '--primary-background-color': theme.background?.color,
      '--primary-foreground-color': theme.foreground?.color,
    };
  }

  public static secondaryStyles(theme: ThemeSecondarySchema | undefined) {
    if (!theme) {
      return {};
    }

    return {
      '--secondary-background-color': theme.background?.color,
      '--secondary-foreground-color': theme.foreground?.color,
    };
  }

  public static buttonStateStyles(theme: ThemeButtonStateSchema | undefined, variant?: string) {
    if (!theme) {
      return {};
    }

    const prefix = variant
      ? `--button-${ variant }`
      : '--button';

    return {
      [`${ prefix }-normal-background-color`]: theme.normal?.background?.color,
      [`${ prefix }-normal-foreground-color`]: theme.normal?.foreground?.color,
      [`${ prefix }-normal-border-color`]: theme.normal?.border?.color,
      [`${ prefix }-active-background-color`]: theme.active?.background?.color,
      [`${ prefix }-active-foreground-color`]: theme.active?.foreground?.color,
      [`${ prefix }-active-border-color`]: theme.active?.border?.color,
    };
  }

  public static buttonVariantStyles(theme: ThemeButtonVariantSchema | undefined) {
    if (!theme) {
      return {};
    }

    return {
      ...ThemeService.buttonStateStyles(theme.primary, 'primary'),
      ...ThemeService.buttonStateStyles(theme.secondary, 'secondary'),
    };
  }

  public static cardStyles(theme: ThemeCardSchema | undefined) {
    if (!theme) {
      return {};
    }

    return {
      '--card-background-color': theme.background?.color,
      '--card-foreground-color': theme.foreground?.color,
      '--card-border-color': theme.border?.color,
      '--card-border-radius': theme.border?.radius,
    };
  }

  public static tabBarStyles(theme: ThemeTabBarSchema | undefined) {
    if (!theme) {
      return {};
    }

    return {
      '--tab-bar-background-color': theme.background?.color,
      '--tab-bar-button-normal-background-color': theme.button?.normal?.background?.color,
      '--tab-bar-button-normal-foreground-color': theme.button?.normal?.foreground?.color,
      '--tab-bar-button-normal-border-color': theme.button?.normal?.border?.color,
      '--tab-bar-button-active-background-color': theme.button?.active?.background?.color,
      '--tab-bar-button-active-foreground-color': theme.button?.active?.foreground?.color,
      '--tab-bar-button-active-border-color': theme.button?.active?.border?.color,
    };
  }

  public static styles(theme: ThemeSchema | undefined) {
    if (!theme) {
      return {};
    }

    return {
      ...ThemeService.baseStyles(theme.base),
      ...ThemeService.primaryStyles(theme.primary),
      ...ThemeService.secondaryStyles(theme.secondary),
      ...ThemeService.buttonVariantStyles(theme.button),
      ...ThemeService.cardStyles(theme.card),
      ...ThemeService.tabBarStyles(theme.tabBar),
    };
  }

}
